import { CheckCircle } from 'lucide-react';

import { useAddons } from '@/react/portainer/addons/queries/useAddons';

import { Alert } from '@@/Alert';
import { Icon } from '@@/Icon';
import { InlineLoader } from '@@/InlineLoader';

/**
 * CatalogStatus reports whether the configured URL yields a usable catalog. It
 * reads the add-ons list, whose catalogError comes from the same fetch the
 * gateway uses, rather than running a parallel check that could disagree.
 *
 * Saving is never blocked on it: an operator may configure a mirror before it
 * is reachable, so this is feedback, not a gate.
 */
export function CatalogStatus() {
  const addonsQuery = useAddons();

  if (addonsQuery.isLoading) {
    return <InlineLoader>Checking the add-on catalog...</InlineLoader>;
  }

  const catalogError = addonsQuery.data?.catalogError;
  if (catalogError) {
    return (
      <Alert color="warn" title="Unable to load the add-on catalog">
        <p className="mb-0">{catalogError}</p>
        <p className="mb-0">
          The URL is saved either way, so this resolves on its own once the
          source is reachable.
        </p>
      </Alert>
    );
  }

  // An empty list with no error is a catalog that loaded and has no entries,
  // which is not the same as one that could not be read.
  const count = addonsQuery.data?.addons?.length ?? 0;

  return (
    <span className="inline-flex items-center gap-1 text-sm text-green-8 th-highcontrast:text-white th-dark:text-green-3">
      <Icon icon={CheckCircle} />
      {count === 1
        ? 'Catalog loaded — 1 add-on available'
        : `Catalog loaded — ${count} add-ons available`}
    </span>
  );
}
